const SAFE_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'P', 'BR', 'DIV', 'UL', 'OL', 'LI', 'SPAN', 'A']);
const DROP_COMPLETELY = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'IFRAME', 'OBJECT']);
const SAFE_INLINE_STYLE_PROPERTIES = ['font-weight', 'font-style', 'text-decoration'] as const;

function copySafeInlineStyles(source: HTMLElement, target: HTMLElement): void {
  SAFE_INLINE_STYLE_PROPERTIES.forEach((property) => {
    const value = source.style.getPropertyValue(property).trim();
    if (value) target.style.setProperty(property, value);
  });
}

function sanitizeNode(
  node: Node,
  ownerDocument: Document,
  preserveInlineStyles: boolean,
): Node | DocumentFragment | null {
  if (node.nodeType === Node.TEXT_NODE) return ownerDocument.createTextNode(node.textContent ?? '');
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();
  if (DROP_COMPLETELY.has(tag)) return null;

  if (!SAFE_TAGS.has(tag)) {
    const fragment = ownerDocument.createDocumentFragment();
    element.childNodes.forEach((child) => {
      const sanitized = sanitizeNode(child, ownerDocument, preserveInlineStyles);
      if (sanitized) fragment.appendChild(sanitized);
    });
    return fragment;
  }

  const clean = ownerDocument.createElement(tag.toLowerCase());
  if (preserveInlineStyles) copySafeInlineStyles(element, clean);
  element.childNodes.forEach((child) => {
    const sanitized = sanitizeNode(child, ownerDocument, preserveInlineStyles);
    if (sanitized) clean.appendChild(sanitized);
  });
  return clean;
}

function htmlToFragment(html: string, ownerDocument: Document, preserveInlineStyles: boolean): DocumentFragment {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const fragment = ownerDocument.createDocumentFragment();
  parsed.body.childNodes.forEach((child) => {
    const sanitized = sanitizeNode(child, ownerDocument, preserveInlineStyles);
    if (sanitized) fragment.appendChild(sanitized);
  });
  return fragment;
}

function plainTextToFragment(text: string, ownerDocument: Document): DocumentFragment {
  const fragment = ownerDocument.createDocumentFragment();
  text.split(/\r?\n/).forEach((line, index, lines) => {
    if (line) fragment.appendChild(ownerDocument.createTextNode(line));
    if (index < lines.length - 1) fragment.appendChild(ownerDocument.createElement('br'));
  });
  return fragment;
}

function normalizeClipboardText(text: string): string {
  return text.replace(/\u00a0/g, ' ').trim();
}

function hasConsistentExactKnownToken(html: string, plainText: string, knownKeys: readonly string[]): boolean {
  const normalizedPlainText = normalizeClipboardText(plainText);
  if (!html || !knownKeys.some((key) => normalizedPlainText === `{{${key}}}`)) return false;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return normalizeClipboardText(parsed.body.textContent ?? '') === normalizedPlainText;
}

export default function sanitizeTemplatePaste(
  dataTransfer: DataTransfer,
  ownerDocument: Document = document,
  knownKeys: readonly string[] = [],
): DocumentFragment {
  const html = dataTransfer.getData('text/html');
  const plainText = dataTransfer.getData('text/plain');
  const preserveInlineStyles = hasConsistentExactKnownToken(html, plainText, knownKeys);
  return html
    ? htmlToFragment(html, ownerDocument, preserveInlineStyles)
    : plainTextToFragment(plainText, ownerDocument);
}
